import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { isDealerUser } from "../lib/roles";

export default function AdminMiscDocuments() {
  const { user, ready } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [label, setLabel] = useState("");
  const [tags, setTags] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (ready && !isDealerUser(user)) navigate("/dealer-login"); }, [ready, user, navigate]);

  const load = async () => {
    const params = q ? { q } : {};
    const { data } = await api.get("/admin/crm/misc", { params });
    setItems(data.items || []);
  };
  useEffect(() => { if (isDealerUser(user)) load(); /* eslint-disable-next-line */ }, [user]);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("label", label);
      fd.append("tags", tags);
      fd.append("file", file);
      await api.post("/admin/crm/misc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLabel(""); setTags(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      load();
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/admin/crm/misc/${id}`); load();
  };

  const view = (it) => {
    const token = localStorage.getItem("ssc_token");
    if (token) {
      fetch(`${API}${it.url}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.blob())
        .then(blob => { const u = URL.createObjectURL(blob); window.open(u, "_blank"); });
    } else {
      window.open(`${API}${it.url}`, "_blank");
    }
  };

  return (
    <div className="ssc-container" style={{ paddingTop: 24, paddingBottom: 48 }} data-testid="admin-misc-docs">
      <div className="flex justify-between items-start mb-3">
        <div>
          <Link to="/admin/crm" className="text-xs text-blue-600 hover:underline">← CRM Search</Link>
          <h1 className="text-2xl font-bold text-[#061426] mt-1">Miscellaneous Documents</h1>
          <p className="text-sm text-gray-500">A shared system folder for forms, references, or any document not tied to a specific customer.</p>
        </div>
      </div>

      <div className="bg-gray-50 border rounded p-3 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2 items-end">
        <Field label="Label"><input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Blank We-Owe template"
          className="w-full border border-gray-300 rounded p-2 text-sm" data-testid="misc-label" /></Field>
        <Field label="Tags (comma)"><input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="forms, templates"
          className="w-full border border-gray-300 rounded p-2 text-sm" data-testid="misc-tags" /></Field>
        <Field label="File" wide>
          <input ref={fileRef} type="file" accept="image/*,.pdf" onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-sm" data-testid="misc-file" />
        </Field>
        <button className="ssc-btn-primary" onClick={upload} disabled={!file || busy} data-testid="misc-upload">{busy ? "Uploading…" : "+ Add Document"}</button>
      </div>

      <div className="flex gap-2 mb-3">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search label, filename, tag"
          className="flex-1 border border-gray-300 rounded p-2 text-sm" data-testid="misc-search" />
        <button className="ssc-btn-secondary" onClick={load} data-testid="misc-search-go">Search</button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="misc-list">
          <thead className="bg-gray-50 text-gray-600">
            <tr><th className="p-2 text-left">Label</th><th className="p-2 text-left">Filename</th>
              <th className="p-2 text-left">Tags</th><th className="p-2 text-left">Uploaded</th><th className="p-2 text-left">By</th><th className="p-2"></th></tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t" data-testid={`misc-row-${it.id}`}>
                <td className="p-2 font-semibold">{it.label}</td>
                <td className="p-2 text-xs">{it.filename}</td>
                <td className="p-2 text-xs">{(it.tags || []).join(", ")}</td>
                <td className="p-2 text-xs text-gray-500">{(it.uploaded_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="p-2 text-xs text-gray-500">{it.uploaded_by_name || ""}</td>
                <td className="p-2 text-right">
                  <button className="text-xs text-blue-600 hover:underline mr-2" onClick={() => view(it)} data-testid={`misc-view-${it.id}`}>View</button>
                  <button className="text-xs text-red-600 hover:underline" onClick={() => del(it.id)} data-testid={`misc-delete-${it.id}`}>Delete</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (<tr><td colSpan={6} className="p-6 text-center text-gray-400">No documents yet.</td></tr>)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Field({ label, children, wide }) {
  return <div className={wide ? "md:col-span-2" : ""}><label className="text-xs text-gray-500">{label}</label>{children}</div>;
}
